import React, { useState, useMemo } from "react";
import { HiXCircle } from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { MdOutlineLocationOn, MdOutlineEmail } from "react-icons/md";
import { StatusBadge } from "../StatusBadge";
import ScrollableContainer from "./ScrollableContainer";
import LanguageSelector from "../LanguageSelector";
import BackButton from "./BackButton";
import { TicketTypeWithId, MyTicket } from "../../../hooks/useEventCardState";
import { ImExit } from "react-icons/im";

type TicketTab = "active" | "used";

interface MyTicketsViewProps {
  myTickets: MyTicket[];
  isLoading: boolean;
  availableTickets: TicketTypeWithId[];
  eventName?: string;
  eventLocation?: string;
  userEmail?: string | null;
  onBack: () => void;
  onLogout: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const MyTicketsView: React.FC<MyTicketsViewProps> = ({
  myTickets,
  isLoading,
  availableTickets,
  eventName,
  eventLocation,
  userEmail,
  onBack,
  onLogout,
  t,
}) => {
  const [activeTab, setActiveTab] = useState<TicketTab>("active");

  const { activeTickets, usedTickets } = useMemo(() => {
    const active = myTickets.filter((ticket) => ticket.status === "active");
    const used = myTickets.filter((ticket) => ticket.status === "used");
    return { activeTickets: active, usedTickets: used };
  }, [myTickets]);

  const displayedTickets = activeTab === "active" ? activeTickets : usedTickets;

  const renderTicketList = (tickets: MyTicket[]) => {
    if (tickets.length === 0) {
      const isActive = activeTab === "active";
      return (
        <div className="text-center py-12">
          <HiXCircle className="mx-auto text-white/40 mb-4" size={64} />
          <h4 className="text-lg font-semibold text-white mb-2">
            {t(
              isActive
                ? "event.myTickets.noActiveTickets"
                : "event.myTickets.noUsedTickets"
            )}
          </h4>
          <p className="text-white/70 text-sm">
            {t(
              isActive
                ? "event.myTickets.noActiveTicketsMessage"
                : "event.myTickets.noUsedTicketsMessage"
            )}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {tickets.map((ticket, index) => {
          const ticketType = availableTickets.find(
            (t) => t.ticket_id === ticket.ticket_id
          );

          return (
            <div
              key={index}
              className={`rounded-xl bg-white border-2 p-4 shadow-sm ${
                ticket.status === "used"
                  ? "border-gray-300 opacity-75"
                  : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    ticket.status === "used" ? "bg-gray-400" : "bg-green-500"
                  }`}
                >
                  <HiTicket className="text-white" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 text-base truncate">
                    {ticketType?.settings.name}
                  </h4>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3 space-y-2">
                {ticketType?.settings.price !== undefined && (
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="font-semibold">
                      {t("event.myTickets.price")}
                    </span>
                    <span>
                      {ticketType.settings.price === 0 && (
                        <StatusBadge
                          color="success"
                          size="sm"
                          emphasis="solid"
                          className="flex-shrink-0"
                        >
                          {t("event.tickets.free")}
                        </StatusBadge>
                      )}
                      {ticketType.settings.price > 0 &&
                        `${ticketType.settings.price} ${ticketType.settings.currency}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ScrollableContainer>
      <div className="flex items-center justify-between mb-4">
        <BackButton
          onClick={onBack}
          label={t("event.purchase.backToTickets")}
        />
        <div className="flex items-center gap-2">
          <button
            onClick={onLogout}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white hover:scale-105 transition-all duration-200"
          >
            <ImExit size={16} />
          </button>
          <LanguageSelector variant="compact" />
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-white mb-1">
          {t("event.myTickets.title")}
        </h2>
        <p className="text-sm text-white/70">{eventName}</p>
        {userEmail && (
          <div className="mt-3 bg-white/10 rounded-lg border border-white/20 p-3 flex items-center gap-2">
            <MdOutlineEmail size={18} className="text-white/70 flex-shrink-0" />
            <span className="text-sm text-white/90 truncate">{userEmail}</span>
          </div>
        )}
        {eventLocation && (
          <div className="mt-3 bg-white/10 rounded-lg border border-white/20 p-3">
            <p className="text-white/90 text-sm whitespace-pre-line flex items-start gap-2">
              <MdOutlineLocationOn size={18} className="flex-shrink-0 mt-0.5" />
              <span className="break-all min-w-0 flex-1">{eventLocation}</span>
            </p>
          </div>
        )}
      </div>

      <nav className="grid grid-cols-2 gap-1 rounded-lg bg-white/10 p-1 mt-4">
        {[
          {
            key: "active" as const,
            label: t("event.myTickets.tabActive"),
            count: activeTickets.length,
          },
          {
            key: "used" as const,
            label: t("event.myTickets.tabUsed"),
            count: usedTickets.length,
          },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`flex items-center justify-center gap-2 rounded-md px-2 py-2.5 text-sm font-semibold transition-all duration-200 ${
              activeTab === key
                ? "bg-white text-gray-900 shadow-sm"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <span className="truncate">{label}</span>
            <span
              className={`text-xs rounded-full px-1.5 py-0.5 ${
                activeTab === key
                  ? "bg-gray-200 text-gray-700"
                  : "bg-white/15 text-white/60"
              }`}
            >
              {count}
            </span>
          </button>
        ))}
      </nav>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <p className="text-white/70 mt-4">{t("event.myTickets.loading")}</p>
        </div>
      ) : myTickets.length === 0 ? (
        <div className="text-center py-12">
          <HiXCircle className="mx-auto text-white/40 mb-4" size={64} />
          <h4 className="text-lg font-semibold text-white mb-2">
            {t("event.myTickets.noTickets")}
          </h4>
          <p className="text-white/70 text-sm">
            {t("event.myTickets.noTicketsMessage")}
          </p>
        </div>
      ) : (
        renderTicketList(displayedTickets)
      )}
    </ScrollableContainer>
  );
};

export default MyTicketsView;
